import { ReactNode, useCallback, useEffect, useState } from 'react';
import { IDivisionSelectorContext } from './interface';
import { useSessionStore } from '../../../context/session';
import { AreaCollection } from '../../../utils/area_collection';

/**
 * 街区プロバイダ情報
 */
export type DivisionProviderData = { divisionId: number, floorId: number, blockId: number };

/**
 * 街区プロバイダのプロパティ
 */
export type DivisionProviderProps = {
  /**
   * 選択される街区IDの初期値を設定
   */
  defaultDivisionId?: number;
  /**
   * 選択されるフロアIDの初期値を設定
   */
  defaultFloorId?: number;
  /**
   * 選択される区画IDの初期値を設定
   */
  defaultBlockId?: number;
  /**
   * localStorageで保存された街区ID、フロアIDを利用する場合trueにする
   */
  useLocalStorageIds?: boolean;
  /**
   * 変更された時に呼ばれるハンドラー
   * @param data
   */
  onChange?: (data: DivisionProviderData) => void;
  children: ReactNode;
};

/**
 * 共通変数を格納するプロバイダ
 * Division, Floor, Block はこの Provider で囲んだ中で利用する必要がある
 * @param context
 * @constructor
 */
export function DivisionSelectorProvider(context: React.Context<IDivisionSelectorContext>): React.FC<DivisionProviderProps> {

  return ({ children, defaultDivisionId, defaultBlockId, defaultFloorId, useLocalStorageIds, onChange }) => {
    const { areasInfo, selectedDivisionId, selectedFloorId } = useSessionStore((selector) => selector);

    const reCalcIds = useCallback((d: DivisionProviderData) => {
      const area = new AreaCollection(areasInfo);
      const res = { ...d };

      if (!area.divisions().contains(res.divisionId)) {
        res.divisionId = 0;
        res.floorId = 0;
        res.blockId = 0;
      } else if (!area.floors(res.divisionId).contains(res.floorId)) {
        res.floorId = 0;
        res.blockId = 0;
      } else if (!area.blocks(res.divisionId, res.floorId).contains(res.blockId)) {
        res.blockId = 0;
      }

      return res;
    }, [areasInfo]);

    const [data, setData] = useState<{ divisionId: number, floorId: number, blockId: number }>(reCalcIds({
      divisionId: defaultDivisionId ?? (useLocalStorageIds ? selectedDivisionId : 0),
      floorId: defaultFloorId ?? (useLocalStorageIds ? selectedFloorId : 0),
      blockId: defaultBlockId ?? 0,
    }));

    useEffect(() => {
      setData(reCalcIds({
        divisionId: defaultDivisionId ?? (useLocalStorageIds ? selectedDivisionId : 0),
        floorId: defaultFloorId ?? (useLocalStorageIds ? selectedFloorId : 0),
        blockId: defaultBlockId ?? 0,
      }));
    }, [defaultDivisionId, defaultFloorId, defaultBlockId, useLocalStorageIds, reCalcIds]);

    const setDivisions = useCallback(({ divisionId, floorId, blockId }: { divisionId?: number, floorId?: number, blockId?: number }) => {
      setData((data) => {
        let res = { ...data };
        if (divisionId !== undefined) {
          res.divisionId = divisionId;
        }
        if (floorId !== undefined) {
          res.floorId = floorId;
        }
        if (blockId !== undefined) {
          res.blockId = blockId;
        }
        res = reCalcIds(res);
        onChange?.(res);
        return res;
      });
    }, [context, onChange, areasInfo]);


    return (
        <context.Provider value={{
          ...data,
          update: setDivisions,
        }}>
          {children}
        </context.Provider>
    );
  };
}
